import { useMonthlyPayment } from "app/hooks/checkout/useMonthlyPayment";
import { useCartContext } from "app/providers/CartProvider";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import ProductModal, { ProductModalType } from "entities/shared/product-modal";
import { opdekdeurSelectableWidths } from "features/option-controls/measurements-option-controls/components/OpdekdeurSelfMontageWidths";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateCartPayload, Product } from "shared/api/middleware-api-client";
import { selfAssemblyInputState } from "shared/store/selfAssembly/selfAssemblyInputs";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useSnapshot } from "valtio";
import { DoorOptionsFooterMobile } from "widgets/configurator/door-options-footer/components/DoorOptionsFooterMobile";
export const MobileConfiguratorFooter = () => {
  const {
    selectedDoor,
    layout,
    color,
    glass,
    handle,
    getConfigurationPrice,
    getConfigurationProduct,
    showBeamHeightValidationError,
    selfAssembleDoorData,
    minDoorHeight,
    minDoorWidth,
    maxDoorHeight,
    maxDoorWidth,
  } = useConfigurationOptionsContext();
  const { addCartProduct, getCart, isSampleSale } = useCartContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const configurationId: string | null = searchParams.get("configuration");
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { data } = useMonthlyPayment(getConfigurationPrice());
  const monthlyPayment = data?.monthlyPayment;

  const selfAssemblyInputsSnapshot = useSnapshot(selfAssemblyInputState);

  const isMonthlyPaymentAvailable = false;

  const validateOptions = (): boolean => {
    let errors: string[] = [];

    if (!selectedDoor) {
      errors.push("deur");
    }

    if (!layout) {
      errors.push("ontwerp");
    }

    if (!color) {
      errors.push("kleur");
    }

    if (!glass) {
      errors.push("glas");
    }

    if (!handle) {
      errors.push("deurbeslag");
    }

    if (errors.length === 0) {
      return true;
    }

    const error = `Je hebt nog geen ${errors.reduce(
      (text, value, i, array) =>
        text + (i < array.length - 1 ? ", " : " en ") + value,
    )}
        geselecteerd. Maak een keuze in het menu voordat je verder gaat.`;
    toast.error(error);

    return false;
  };

  const errorAssemblyAddition = (
    cart: CreateCartPayload,
    productToAdd: Product,
  ) => {
    if (cart.products.length > 0) {
      if (
        cart.isSelfAssembly === false &&
        productToAdd.isSelfAssembly === undefined
      ) {
        return false;
      }
      return cart.isSelfAssembly !== productToAdd.isSelfAssembly;
    }
  };

  const canProceed = (productToAdd: Product) => {
    const cart = getCart();
    if (cart.standardProducts.length > 0) {
      toast.info(
        "Je kan geen deur toevoegen aan de winkelwagen, omdat je andere producttypes in de winkelwagen hebt zitten.",
      );
      return false;
    }

    if (errorAssemblyAddition(cart, productToAdd)) {
      toast.info(
        "Je kunt geen deur met montageservice aan je winkelwagen toevoegen omdat je bij een ander product al hebt gekozen voor `zelf monteren`.",
      );
      return false;
    }
    if (isSampleSale) {
      toast.info(
        "Let op: er staat al een sample sale product in je winkelwagen. Je kunt dit product niet combineren.",
      );
      return false;
    }
    if (showBeamHeightValidationError) {
      toast.error(
        "Let op: maximale lengte greep is 2100mm. Pas het ontwerp eerst aan.",
      );
      return false;
    }
    return true;
  };

  const schuifdeurFieldsValidation = (product: Product) => {
    if (product.measurements.slideToWall === undefined) {
      toast.info("Kies een schuifdeur positie");
      return false;
    }

    if (product.measurements.locationPlacement === undefined) {
      toast.info("Kies een montagewijze rails");
      return false;
    }

    if (
      product.measurements.sponningsHoogte === undefined ||
      product.measurements.sponningsHoogte < minDoorHeight ||
      product.measurements.sponningsHoogte > maxDoorHeight
    ) {
      toast.info("Let op: de hoogte sparing naar plafond is niet geschikt.");
      return false;
    }

    if (
      product.measurements.sponningsBreedte === undefined ||
      product.measurements.sponningsBreedte < minDoorWidth ||
      product.measurements.sponningsBreedte > maxDoorWidth
    ) {
      toast.info("Let op: de breedte van de deur is niet geschikt.");
      return false;
    }

    if (product.measurements.locationPlacement === "wand") {
      if (
        product.measurements.sparingPlafond === undefined ||
        product.measurements.sparingPlafond < 85
      ) {
        toast.info("Let op: de hoogte sparing naar plafond is niet geschikt.");
        return false;
      }
    }

    return true;
  };

  const opdekdeurTaatsdeurFieldsValidation = (product: Product) => {
    if (
      product.measurements.height === undefined ||
      product.measurements.height < minDoorHeight ||
      product.measurements.height > maxDoorHeight
    ) {
      toast.info("Let op: de hoogte van de deur is niet geschikt.");
      return false;
    }

    if (
      product.measurements.sponningsBreedte === undefined ||
      product.measurements.sponningsBreedte < minDoorWidth ||
      product.measurements.sponningsBreedte > maxDoorWidth
    ) {
      toast.info("Let op: de breedte van de deur is niet geschikt.");
      return false;
    }

    if (product.doorType.code === "opdekdeur") {
      if (
        !opdekdeurSelectableWidths.includes(
          product.measurements.sponningsBreedte,
        )
      ) {
        toast.info("Let op: u dient de deurbreedte te selecteren.");
        return false;
      }

      //Hinge validation: hingeamount:2 -> door height over 2269mm -> hinge1 and hinge2 should be filled
      if (
        product.measurements.hingeAmount === 2 &&
        (product.measurements.hinge1 === 0 || product.measurements.hinge2 === 0)
      ) {
        toast.info(
          "Pas het aantal scharnieren aan om de deur te kunnen bestellen",
        );
        return false;
      }
    }

    return true;
  };

  const selfAssemblyWidthHeightValidation = () => {
    if (!selfAssemblyInputsSnapshot.widthInput) {
      toast.info("Let op: de breedte van de deur is niet geschikt.");
      return false;
    }

    if (!selfAssemblyInputsSnapshot.heightInput) {
      toast.info("Let op: de hoogte van de deur is niet geschikt.");
      return false;
    }
    return true;
  };

  const addToCart = () => {
    if (!validateOptions()) return;
    const product = getConfigurationProduct();

    if (!product) return;
    if (!canProceed(product)) return;

    if (product.isSelfAssembly) {
      if (!selfAssemblyWidthHeightValidation()) return;
    }

    if (
      product.isSelfAssembly &&
      (product.doorType.code === "opdekdeur" ||
        product.doorType.code === "taatsdeur") &&
      !opdekdeurTaatsdeurFieldsValidation(product)
    )
      return;

    if (
      product.isSelfAssembly &&
      product.doorType.code === "schuifdeur" &&
      !schuifdeurFieldsValidation(product)
    )
      return;

    let id: number | undefined = Number(configurationId);
    if (!configurationId || isNaN(id)) id = undefined;

    if (!addCartProduct(product, id)) return;
    setConfirmationModalOpen(true);
  };

  const product = getConfigurationProduct();
  return (
    <>
      {product && (
        <ProductModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setConfirmationModalOpen}
          type={ProductModalType.DOOR}
          monthlyPayment={monthlyPayment}
          totalPrice={getFormattedPrice(getConfigurationPrice())}
          product={product}
        />
      )}

      <div
        id="footersGridElements"
        className="gg-container gg-fixed gg-bottom-0 gg-left-0 gg-z-50 gg-order-last gg-mx-auto gg-w-full"
      >
        <DoorOptionsFooterMobile
          selectedDoor={selectedDoor}
          isMonthlyPaymentAvailable={isMonthlyPaymentAvailable}
          monthlyPayment={monthlyPayment}
          addToCart={addToCart}
          getConfigurationPrice={getConfigurationPrice}
          isSelfAssembly={selfAssembleDoorData.selfAssembly}
        />
      </div>
    </>
  );
};
